import Clipboard from "@react-native-clipboard/clipboard";
import useApi from "@toothfairy/shared-api/useApi";
import { AppText } from "@toothfairy/shared-ui";
import AppActivityIndicator from "@toothfairy/shared-ui/AppActivityIndicator";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppSecureTextInput from "@toothfairy/shared-ui/AppSecureTextInput";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, View } from "react-native";
import envConfig from "../../../envConfig";
import Files from "../../API/Files";
import useWorkspaces from "../../Hooks/useWorkspaces";
import AdminInfoLeaf from "../../Screens/Admin/Components/SubComponents/AdminInfoLeaf";
import utils from "../../Screens/Common/utils";
import { isLargeScreen, isMobileScreen } from "../../Screens/MainViewContainer";
import SavingNotification from "../TrainSwitcher/SavingNotification";
import DownloadModalContent from "./DownloadModalContent";
import ToothFairyButton from "./ToothFairyButton";
import ToothFairyClickableOption from "./ToothFairyClickableOption";
import ToothFairyJsonViewer from "./ToothFairyJsonViewer";
import ToothFairyLoadingPlaceholder from "./ToothFairyLoadingPlaceholder";
import ToothFairyModal from "./ToothFairyModal";
import ToothFairyResponsiveOptionsModal from "./ToothFairyResponsiveOptionsModal";
import ToothFairySupportButton from "./ToothFairySupportButton";
import ToothFairyUrlViewer from "./ToothFairyUrlViewer";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import Constants from "../../Constants";

function removeRCFromKeys(obj) {
  // If input is not an object or is null, return as is
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((item) => removeRCFromKeys(item));
  }

  // Handle objects
  return Object.entries(obj).reduce((acc, [key, value]) => {
    // Remove RC from key if it exists
    const newKey = key.endsWith("RC") ? key.slice(0, -2) : key;
    // Recursively process the value
    acc[newKey] = removeRCFromKeys(value);
    return acc;
  }, {});
}

function removeBatchRelatedKeys(obj) {
  if (obj)
    return {
      agentId: obj?.agentId,
      name: obj?.name,
      weights: obj?.weights,
      reviewerAgentID: obj?.reviewerAgentID,
      benchmarkID: obj?.benchmarkID,
    };
  else {
    return null;
  }
}

const ToothFairyOutput = ({
  isOutputExpanded,
  parsingInProgress,
  data,
  subCaption,
  isInModal,
  isOutputUpToDate,
  checkLength = true,
  isEventDriven = false,
  supportId,
  supportUrl,
  title = "Details",
  resultObjectTitle = "Request response",
  responseCopyBtnTitle = "Copy response",
  resultObjectIcon,
  requestPayload,
  path,
  method,
  trainingLogId,
  batchLogId,
  handleDataDownload,
  markTrainingLogAsActive,
  trainingInProgress,
  isSwapingInProgress,
  urlInputWidth,
  requestBodyType = "json",
  configObjectTitle,
  configObjectIcon,
  configObject,
  includeConfig = false,
  isSwapRequested,
  datasetOnly,
  showSummary = false,
  name,
  description = "No description",
  handleFieldUpdate = () => {},
  handleTrainingInitiation = () => {},
  handleFineTuningStart = () => {},
  handleFineTuningSetupOpening = () => {},
  createdBy,
  lastChangedAt,
  meta,
  trainingLogData,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const [isModalOptionsOpen, setIsModalOptionsOpen] = React.useState(false);
  const { state } = AppStateManager.useAppStateManager();
  const [enhancedTrainingLog, setEnhancedTrainingLog] = useState(null);
  const _styles = styleGenerator({
    currentTheme,
    flexDirection: "column",
    isOutputExpanded,
    isInModal,
  });
  const [isEditable, setIsEditable] = React.useState(false);
  const [isDescrEditable, setIsDescrEditable] = React.useState(false);
  const {
    getActiveWorkspace,
    getTrainingLogInfo,
    getTypeFromTrainingType,
    getBatchLogInfo,
    getUserName,
  } = useWorkspaces();
  const [trainingMetrics, setTrainingMetrics] = React.useState(null);
  const [trainingMetricsUrl, setTrainingMetricsUrls] = React.useState(null);
  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useApi(Files.downloadUrlGeneration, envConfig);
  const [selection, setSelection] = React.useState([]);
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { isTrainingLogIdActive } = useWorkspaces();
  const {
    data: filesResult,
    error: filesError,
    loading: filesInProgress,
    apiRequest: _filesRequest,
    response: filesResponse,
  } = useApi(Files.getDocumentsByLogId, envConfig);
  const handleClickOnTrain = () => {
    if (getActiveWorkspace()?.subscriptionType != "enterprise") {
      return AppToasty.show(
        "Upgrade to enterprise to generate and run fine-tuned models in ToothFairyAI",
        {
          placement: "bottom",
          type: "danger",
        }
      );
    } else handleFineTuningSetupOpening(trainingLogId);
  };
  const defaultOnUtilityIconClick = (type) => {
    Clipboard.setString(
      JSON.stringify(type === "request" ? requestPayload : data)
    );
    AppToasty.show(t("JSONcopyCaption"), {
      type: "success",
    });
  };
  useEffect(() => {
    if (!isModalOptionsOpen || !isModalOpen) setSelection([]);
  }, [isModalOptionsOpen, isModalOpen]);

  useEffect(() => {
    return () => {
      setSelection([]);
    };
  }, []);

  useEffect(() => {
    let trainingMeta = enhancedTrainingLog?.trainingMeta;
    if (typeof enhancedTrainingLog?.trainingMeta == "string")
      trainingMeta = JSON.parse(enhancedTrainingLog?.trainingMeta);
    if (trainingMeta?.metrics_imgs && trainingMeta?.metrics_imgs.length > 0) {
      setTrainingMetrics(trainingMeta?.metrics_imgs);
    } else {
      setTrainingMetrics([]);
    }
  }, [enhancedTrainingLog?.trainingMeta]);

  useEffect(() => {
    const fetchUrls = async () => {
      try {
        if (!trainingMetrics) return;

        const promises = trainingMetrics.map((u) =>
          S3downloadUrlRequest({
            filename: u?.split("/").at(-1),
            context: "training",
            workspaceid: getActiveWorkspace()?.id,
            type: "metrics",
            logId: enhancedTrainingLog?.id,
          })
        );

        const urls = await Promise.all(promises);
        setTrainingMetricsUrls(urls);
      } catch (error) {
        console.error("Error fetching training metrics URLs:", error);
      }
    };

    fetchUrls();
  }, [trainingMetrics, getActiveWorkspace()?.id]);

  const isUseModelEnabled =
    trainingInProgress ||
    isSwapingInProgress ||
    isSwapRequested ||
    data?.errorCode ||
    datasetOnly ||
    !isTrainingLogIdActive(trainingLogId);

  const generateModelData = (item, datasetOnly) => {
    if (
      item?.status === "completed" ||
      item?.status === "swapping" ||
      item?.status === "swapModelRequested" ||
      item?.status === "swapModelFailed"
    ) {
      if (item?.trainingConfig?.datasetOnly)
        return <AppText>Dataset only</AppText>;
      else if (item?.type === "nlpTraining") {
        return (
          <>
            <AppText>
              {`NER: (${utils.roundToTwo(
                item?.trainingMeta?.trainingScores?.ents_r
              )} - ${utils.roundToTwo(
                item?.trainingMeta?.trainingScores?.ents_f
              )} - ${utils.roundToTwo(
                item?.trainingMeta?.trainingScores?.ents_p
              )} )`}
            </AppText>
            <AppText>
              {`Intent: (${utils.roundToTwo(
                item?.trainingMeta?.trainingScores?.cats_macro_r
              )} - ${utils.roundToTwo(
                item?.trainingMeta?.trainingScores?.cats_macro_f
              )} - ${utils.roundToTwo(
                item?.trainingMeta?.trainingScores?.cats_macro_p
              )} )`}
            </AppText>
          </>
        );
      } else if (
        item?.type === "readComprehensionTraining" ||
        item?.type === "DPO" ||
        item?.type === "SFT"
      ) {
        if (item?.trainingMeta?.metrics) {
          const trainingMeta = JSON.parse(item?.trainingMeta?.metrics);
          const lastItem = trainingMeta[trainingMeta.length - 1];
          return (
            <>
              <AppText>
                {`Model name: ${item?.trainingMeta?.t_model_id}`}
              </AppText>
              <AppText>
                {`Training Loss: (${utils.roundToTwo(
                  lastItem?.training_loss * 100
                )}%)`}
              </AppText>
              <AppText>
                {`Completion accuracy: (${utils.roundToTwo(
                  lastItem?.training_sequence_accuracy * 100
                )}%)`}
              </AppText>
              <AppText>
                {`Token prediction: (${utils.roundToTwo(
                  lastItem?.training_token_accuracy * 100
                )}%)`}
              </AppText>
            </>
          );
        }
      }
    }
  };
  const getTypeCode = (type) => {
    if (type === "nlpTraining") return "nlp";
    else if (type === "readComprehensionTraining") return "rc";
    else if (type === "DPO") return "gn";
    else if (type === "SFT") return "cn";
  };
  const fetchData = async (trainingLogId, batchLogId) => {
    let type, context;
    const workspaceid = getActiveWorkspace()?.id;
    if (workspaceid && (trainingLogId || batchLogId) && requestPayload) {
      if (trainingLogId) {
        const _trainingLog = getTrainingLogInfo(trainingLogId);
        type = getTypeFromTrainingType(
          _trainingLog?.type,
          getTypeCode(_trainingLog?.type)
        ).toLowerCase();
      } else if (batchLogId) {
        type = getBatchLogInfo(batchLogId)?.type?.toLowerCase();
      }
      context = trainingLogId ? "training" : "batch";
      console.log("Batch log", batchLogId);
      const result = await _filesRequest(
        workspaceid,
        trainingLogId || batchLogId,
        type,
        context
      );
    }
  };
  useEffect(() => {
    if (isModalOpen)
      fetchData(
        trainingLogId,
        batchLogId,
        getActiveWorkspace()?.id,
        requestPayload
      );
  }, [
    trainingLogId,
    batchLogId,
    getActiveWorkspace()?.id,
    requestPayload?.input?.type,
    requestPayload?.type,
    isModalOpen,
  ]);
  handleFormSubmit = () => {
    let type, context;
    if (trainingLogId) context = "training";
    else if (batchLogId) context = "batch";

    if (context === "training") {
      const trainingLog = getTrainingLogInfo(trainingLogId);
      type = getTypeFromTrainingType(
        trainingLog?.type,
        getTypeCode(trainingLog?.type)
      ).toLowerCase();
    } else if (context === "batch")
      type = getTypeFromTrainingType(requestPayload?.type).toLowerCase();
    handleDataDownload({
      workspaceid: getActiveWorkspace()?.id,
      logId: context === "training" ? trainingLogId : batchLogId,
      type,
      selection,
    });
  };

  const getChipColor = (type) => {
    if (type === "json") {
      return "primary";
    }
    if (type === "form-data") {
      return "green";
    }
    return "primary";
  };
  const getType = () => {
    if (batchLogId) return "bm";
    else if (trainingLogId) {
      const _trainingLog = getTrainingLogInfo(trainingLogId);
      return getTypeFromTrainingType(
        _trainingLog?.type,
        getTypeCode(_trainingLog?.type)
      ).toLowerCase();
    }
  };
  const handleUseModel = () => {
    try {
      markTrainingLogAsActive(trainingLogId);
      AppToasty.show(t("ActivationChangeRequestCaption"), {
        placement: "bottom",
        type: "success",
      });
    } catch (e) {
      AppToasty.show(t("ActivationChangeRequestCaptionFailed"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };
  const extractModelName = (trainingLogData) => {
    let model_name = "";
    if (trainingLogData?.trainingMeta) {
      let _data = trainingLogData?.trainingMeta;
      if (typeof trainingLogData?.trainingMeta === "string") {
        _data = JSON.parse(trainingLogData?.trainingMeta);
      }
      model_name = _data?.t_model_id;
    }
    return model_name;
  };
  const _actionsChoice = () => {
    return (
      <ToothFairyResponsiveOptionsModal
        isModalOpen={isModalOptionsOpen}
        setIsModalOpen={setIsModalOptionsOpen}
        modalTitle={"Actions"}
      >
        {(trainingLogId || batchLogId) && (
          <>
            <ToothFairyClickableOption
              leftIcon={AppAssets.icons.save}
              onPress={() => setIsModalOpen(true)}
            >
              Download
            </ToothFairyClickableOption>
            {trainingLogId &&
              getTrainingLogInfo(trainingLogId)?.status == "datasetReady" && (
                <ToothFairyButton title="Train" onPress={handleClickOnTrain} />
              )}
          </>
        )}
      </ToothFairyResponsiveOptionsModal>
    );
  };
  const _downloadAppChoice = useMemo(() => {
    return (
      <ToothFairyModal
        modalScrollContainer={{
          justifyContent: "flex-start",
          paddingHorizontal: 0,
        }}
        modalBackdrop={true}
        modalBackdropStatic={true}
        modalStyle={[
          _styles.downloadModal,
          {
            height: filesResult?.Contents?.length < 3 && 410,
          },
        ]}
        modalTitle={"Download Data"}
        isCloseButtonVisible
        closeModal={() => {
          setIsModalOpen(false);
        }}
        isModalVisible={isModalOpen}
        transparent={true}
      >
        <View style={_styles.downloadModalContainer}>
          {(filesInProgress || !filesResult) && (
            <View style={_styles.loadingContainer}>
              <ToothFairyLoadingPlaceholder
                caption="Loading available files..."
                style={_styles.loadingPlaceholder}
              />
            </View>
          )}
          {(batchLogId || trainingLogId) && !filesInProgress && filesResult && (
            <DownloadModalContent
              context={batchLogId ? "batch" : "training"}
              filesResult={filesResult}
              selection={selection}
              onSelectionChange={(v) => {
                setSelection([...v]);
              }}
              type={getType()?.toUpperCase()}
              handleFormSubmit={handleFormSubmit}
            />
          )}
        </View>
      </ToothFairyModal>
    );
  }, [
    isModalOpen,
    selection,
    JSON.stringify(requestPayload),
    filesInProgress,
    batchLogId,
    trainingLogId,
    JSON.stringify(getTrainingLogInfo(trainingLogId)),
  ]);
  useEffect(() => {
    if (trainingLogData?.id) {
      let _data = state.trainingLogs?.find((u) => u?.id == trainingLogData?.id);
      if (typeof _data?.trainingConfig == "string")
        _data.trainingConfig = JSON.parse(_data.trainingConfig);
      setEnhancedTrainingLog(_data);
    } else {
      setEnhancedTrainingLog(null);
    }
  }, [trainingLogData?.id, JSON.stringify(state.trainingLogs)]);
  return (
    <View style={_styles.outputContainer}>
      <View style={{ flexDirection: "row" }}>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            flex: 1,
          }}
        >
          <View
            style={{
              flexDirection: "row",
            }}
          >
            <AppText
              wrapperStyle={{
                marginBottom: 0,
                marginTop: 0,
                justifyContent: "flex-start",
                paddingVertical: 5,
              }}
              style={_styles.title}
            >
              {trainingLogId
                ? "Fine-tuning details"
                : batchLogId
                ? "Run details"
                : title}
            </AppText>
            {isLargeScreen() ? (
              <>
                {(batchLogId || trainingLogId) && (
                  <ToothFairyButton
                    onPress={() => {
                      setIsModalOpen(true);
                    }}
                    title={"Download"}
                  />
                )}
                {trainingLogId &&
                  getTrainingLogInfo(trainingLogId)?.status ==
                    "datasetReady" && (
                    <ToothFairyButton
                      title="Train"
                      onPress={handleClickOnTrain}
                    />
                  )}
              </>
            ) : (
              <ToothFairyButton
                onPress={() => {
                  setIsModalOptionsOpen(true);
                }}
                title="Actions"
              />
            )}
          </View>

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {!isOutputUpToDate && data && (
              <SavingNotification
                color={"warning"}
                dotValue={1}
                label={"DataNotInSyncCaption"}
              />
            )}
            <ToothFairySupportButton
              _pageId={supportId}
              _url={`${supportUrl}/i/${supportId}`}
            />
          </View>
        </View>
      </View>

      {parsingInProgress ? (
        <AppActivityIndicator
          backgroundColor={currentTheme?.theme?.transparent}
          visible={true}
        />
      ) : (
        <View>
          {showSummary && (
            <AppExpander
              style={{ minWidth: 250 }}
              isExpandedOnLoad={true}
              title="Summary"
              icon={AppAssets.icons.summary}
            >
              <View
                style={{
                  flexDirection: isMobileScreen() ? "row" : "column",
                  paddingLeft: 10,
                  paddingBottom: 10,
                }}
              >
                <AppText
                  style={{
                    fontFamily: "boldFont",
                    color: currentTheme?.theme?.primary,
                  }}
                  wrapperStyle={{
                    marginRight: 10,
                  }}
                >
                  ID
                </AppText>

                <AdminInfoLeaf
                  wrapperStyle={{
                    alignSelf: "flex-start",
                  }}
                  value={batchLogId || trainingLogId || "No id"}
                  label="ID"
                  marginLeft={0}
                  showSimpleText={true}
                />
              </View>
              <View
                style={{
                  paddingLeft: 10,
                  maxWidth: 515,
                }}
              >
                <AppSecureTextInput
                  showTextInputLabel
                  label="Name"
                  placeholder="trainNamePlaceholder"
                  {..._styles.secureInputStyle}
                  type="TEXT"
                  textContainer={{
                    marginRight: 20,
                  }}
                  wrapperInputStyle={{
                    flex: 1,
                  }}
                  coreViewStyle={{
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                  actionsButtonsViewStyle={{
                    marginLeft: 10,
                  }}
                  labelStyle={{
                    minWidth: isMobileScreen() ? 220 : 310,
                  }}
                  value={name}
                  isEditable={false}
                  showEditButtons={false}
                  initialValue={name}
                />
                <AppSecureTextInput
                  {..._styles.secureInputStyle}
                  showTextInputLabel
                  label="Description"
                  multiline={true}
                  numberOfLines={4}
                  placeholder="trainDescrPlaceholder"
                  type="text"
                  textContainer={{
                    paddingTop: 10,
                    marginRight: 20,
                  }}
                  wrapperInputStyle={{
                    flex: 1,
                  }}
                  coreViewStyle={{
                    justifyContent: "flex-start",
                  }}
                  actionsButtonsViewStyle={{
                    marginLeft: 10,
                  }}
                  labelStyle={{
                    minWidth: isMobileScreen() ? 220 : 310,
                  }}
                  value={description || "No description"}
                  isEditable={false}
                  showEditButtons={false}
                  initialValue={description}
                />
                <AppSecureTextInput
                  showTextInputLabel
                  label="Created by"
                  {..._styles.secureInputStyle}
                  type="TEXT"
                  value={name}
                  isEditable={false}
                  isEditEnabled={false}
                  initialValue={createdBy}
                />
                <AppSecureTextInput
                  showTextInputLabel
                  label="Last update"
                  {..._styles.secureInputStyle}
                  type="TEXT"
                  value={name}
                  isEditable={false}
                  isEditEnabled={false}
                  initialValue={lastChangedAt}
                />
                <AppSecureTextInput
                  showTextInputLabel
                  label="Duration in minutes"
                  {..._styles.secureInputStyle}
                  type="TEXT"
                  value={name}
                  isEditable={false}
                  isEditEnabled={false}
                  initialValue={
                    batchLogId
                      ? meta?.duration &&
                        parseFloat((meta?.duration || 0) / 60).toFixed(2)
                      : trainingLogId && trainingLogData?.duration
                  }
                />
                {trainingLogId && (
                  <AppSecureTextInput
                    showTextInputLabel
                    label="Base model"
                    {..._styles.secureInputStyle}
                    type="TEXT"
                    value={name}
                    isEditable={false}
                    isEditEnabled={false}
                    initialValue={
                      Constants.baseModels?.find(
                        (u) =>
                          u?.id ==
                          enhancedTrainingLog?.trainingConfig?.baseModel
                      )?.label || enhancedTrainingLog?.trainingConfig?.baseModel
                    }
                  />
                )}

                {trainingLogId &&
                  getTrainingLogInfo(trainingLogId)?.status == "completed" && (
                    <>
                      <AppText
                        style={{
                          fontFamily: "boldFont",
                        }}
                        wrapperStyle={{
                          marginRight: 10,
                        }}
                      >
                        {`Finetuned model name: ${extractModelName(
                          getTrainingLogInfo(trainingLogId)
                        )}`}
                      </AppText>
                    </>
                  )}
              </View>
            </AppExpander>
          )}
          {batchLogId &&
            ["completed", "inError"].includes(
              getBatchLogInfo(batchLogId)?.status
            ) && (
              <AppExpander
                style={{ minWidth: 250 }}
                isExpandedOnLoad={false}
                title={"Run config"}
                icon={AppAssets.icons.response}
              >
                <View
                  style={{
                    paddingLeft: 30,
                  }}
                >
                  <ToothFairyJsonViewer
                    data={removeBatchRelatedKeys(
                      getBatchLogInfo(batchLogId)?.batchConfig
                    )}
                    isEmpty={false}
                    subCaption={"Detailed run configuration"}
                  />
                </View>
              </AppExpander>
            )}
          {batchLogId &&
            ["completed", "inError"].includes(
              getBatchLogInfo(batchLogId)?.status
            ) && (
              <AppExpander
                style={{ minWidth: 250 }}
                isExpandedOnLoad={false}
                title={"Run output"}
                icon={AppAssets.icons.ai}
              >
                <View
                  style={{
                    paddingLeft: 30,
                  }}
                >
                  <ToothFairyJsonViewer
                    data={getBatchLogInfo(batchLogId)?.batchMeta}
                    isEmpty={false}
                    subCaption={"Detailed run output"}
                  />
                </View>
              </AppExpander>
            )}
          {trainingLogId &&
            ["datasetReady", "completed", "inError"].includes(
              getTrainingLogInfo(trainingLogId)?.status
            ) && (
              <AppExpander
                style={{ minWidth: 250 }}
                isExpandedOnLoad={false}
                title={"Training config"}
                icon={AppAssets.icons.response}
              >
                <View
                  style={{
                    paddingLeft: 30,
                  }}
                >
                  <ToothFairyJsonViewer
                    data={removeRCFromKeys(
                      getTrainingLogInfo(trainingLogId)?.trainingConfig
                    )}
                    isEmpty={
                      !((checkLength && data?.length > 0) || !checkLength)
                    }
                    subCaption={"Detailed training configuration"}
                  />
                </View>
              </AppExpander>
            )}

          {trainingLogId &&
            (getTrainingLogInfo(trainingLogId)?.status == "inError" ||
              getTrainingLogInfo(trainingLogId)?.status == "completed") && (
              <>
                <AppExpander
                  style={{ minWidth: 250 }}
                  isExpandedOnLoad={false}
                  title={"Training dashboard"}
                  icon={AppAssets.icons.dashboard}
                >
                  <View
                    style={{
                      paddingLeft: 30,
                    }}
                  >
                    {trainingMetricsUrl?.map((u) => {
                      return <img src={u?.url} style={{ width: "100%" }} />;
                    })}
                  </View>
                </AppExpander>
                <AppExpander
                  style={{ minWidth: 250 }}
                  isExpandedOnLoad={false}
                  title={"Training output"}
                  icon={AppAssets.icons.advancedReasoning}
                >
                  <View
                    style={{
                      paddingLeft: 30,
                    }}
                  >
                    <ToothFairyJsonViewer
                      data={
                        typeof enhancedTrainingLog?.trainingMeta != "string"
                          ? enhancedTrainingLog?.trainingMeta
                          : JSON.parse(enhancedTrainingLog?.trainingMeta)
                      }
                      isEmpty={false}
                      subCaption={"Detailed training output"}
                    />
                  </View>
                </AppExpander>
              </>
            )}
        </View>
      )}
      {path == "trainingdata" && (
        <ToothFairyJsonViewer
          data={data}
          isEmpty={data?.trainingData == null}
          title={"Training data"}
          subCaption={
            "Start adding some training blocks to see the data structure here"
          }
        />
      )}
      {_downloadAppChoice}
      {_actionsChoice()}
    </View>
  );
};

const styleGenerator = ({
  currentTheme,
  flexDirection,
  isOutputExpanded,
  isInModal,
}) => {
  return StyleSheet.create({
    title: {
      fontSize: currentTheme?.theme?.medium_font_size,
      fontWeight: "bold",
      // color: currentTheme?.theme?.white,
    },
    outputContainer: {
      flex: flexDirection === "row" ? 0.3 : null,
      paddingHorizontal: flexDirection === "row" ? 20 : 10,
      marginTop: flexDirection === "row" || isInModal ? 10 : 20,
      overflow: !isOutputExpanded ? "hidden" : "auto",
      height: isInModal && "100%",
      flexBasis: !isInModal && 250,
      minWidth: "100%",
      zIndex: -1,
    },
    modalInput: {
      height: 320,
      marginHorizontal: "auto",
      marginVertical: "auto",
      width: 600,
    },
    downloadModal: {
      height: 450,
      marginHorizontal: "auto",
      marginVertical: "auto",
      width: 650,
      borderRadius: 12,
      shadowColor: "#000",
      shadowOffset: {
        width: 0,
        height: 4,
      },
      shadowOpacity: 0.15,
      shadowRadius: 8,
      elevation: 8,
    },
    downloadModalContainer: {
      flex: 1,
      paddingTop: 10,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      paddingVertical: 40,
    },
    loadingPlaceholder: {
      paddingTop: 0,
    },
    secureInputStyle: {
      textInputLabelStyle: {
        marginLeft: 0,
        marginBottom: 10,
      },
      style: {
        marginTop: 2,
      },
      wrapperStyleSecureTextInput: {
        maxWidth: 850,
        minWidth: 350,
        marginBottom: 10,
        marginRight: 15,
        flex: 1,
      },
      wrapperInputStyle: {
        flex: 1,
        marginRight: 10,
      },
      textInputLabelStyleCore: {
        marginBottom: 10,
      },
      textContainer: {
        margin: 0,
        padding: 10,
      },
    },
  });
};

export default ToothFairyOutput;
